import { ChoiceChips, ToggleChips } from '../ui/bits';
import { useEffect, useRef, useState } from 'preact/hooks';
import { INTEREST_HINTS, interestHint } from '../book/changes';
import { AUDIENCE_CHOICES, LITERARY_CHOICES, genreChoices, genreFits, type Audience, type GenreOption, type Literary } from '../book/genres';
import { NOT_FOR_ME_CHIPS } from '../book/changes';
import { FuzzyEditor, type Fuzzy } from '../book/edit';
import { Stars } from '../book/reading';
import { bookLabel, statusName } from '../book/model';
import { navigate, useRoute } from '../lib/router';
import { useFindBooks } from '../shell/FindBook';
import { useNotice } from '../shell/notice';
import { Cover, InterestDots } from '../ui/bits';
import {
  COPY_CHOICES,
  DEFAULT_COPY_DETAILS,
  DEFAULT_SHELF,
  MORE_CHOICES,
  SHELF_CHOICES,
  addBook,
  addButton,
  addDetails,
  alreadyLabel,
  defaultCopy,
  defaultLiterary,
  loadGenres,
  detailsFor,
  foundLine,
  ordered,
  placeLine,
  searchFor,
  shelfEdge,
  shelfName,
  splitAuthors,
  storeFor,
  undoAdd,
  type AddShelf,
  type CopyDetails,
  type CopyKey,
  type Found,
  type SearchAnswer,
  type ShelfEdge,
} from './add';
import { FromField, hasFrom } from '../book/gotFrom';
import './add.css';

// Add a book (the + in the header; agreed with Kristi Oct 28; decisions doc › "From the build:
// Add a book"). One screen: find it, your copy, where it goes. A panel over the page on a laptop
// and iPad; a full screen on the phone, Cancel and the main button pinned at the bottom.

/** The outside search waits for a pause in typing (not every letter). */
const PAUSE_MS = 900;

/** A book on a list (Discover Part 3b): Add a book starts on it, with why from the list. */
export interface FromList {
  entry: string;
  title: string;
  author: string;
  why: string;
  literary: Literary | null;
}

export function AddBook({ onClose, fromList }: { onClose: () => void; fromList?: FromList }) {
  const notice = useNotice();
  const route = useRoute();
  const [text, setText] = useState(fromList ? [fromList.title, fromList.author].filter(Boolean).join(' ') : '');
  const [answer, setAnswer] = useState<SearchAnswer | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const searched = useRef('');
  const [picked, setPicked] = useState<Found | null>(null);
  const [manual, setManual] = useState<{ title: string; authors: string } | null>(null);
  const [copy, setCopy] = useState<CopyKey>('none');
  const [details, setDetails] = useState<CopyDetails>(DEFAULT_COPY_DETAILS);
  const [showDetails, setShowDetails] = useState(false);
  const [shelf, setShelf] = useState<AddShelf>(DEFAULT_SHELF);
  const [place, setPlace] = useState('bottom');
  const [n, setN] = useState('');
  const [edge, setEdge] = useState<ShelfEdge | null>(null);
  const [literary, setLiterary] = useState<Literary | null>(fromList?.literary ?? null);
  const [audience, setAudience] = useState<Audience | null>('adult');
  const [genres, setGenres] = useState<string[]>([]);
  const [allGenres, setAllGenres] = useState<GenreOption[]>([]);
  const [needLiterary, setNeedLiterary] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [ended, setEnded] = useState<Fuzzy>({ on: null, precision: null, words: null });
  const [rating, setRating] = useState<number | null>(null);
  const [readAgain, setReadAgain] = useState(false);
  const [nfmChips, setNfmChips] = useState<string[]>([]);
  const [nfmReason, setNfmReason] = useState('');
  const [interest, setInterest] = useState<number | null>(null);
  const [why, setWhy] = useState(fromList?.why ?? '');
  const [rec, setRec] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<{ message: string; label: number | null } | null>(null);
  const onList = useFindBooks(picked || manual ? '' : text);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => inputRef.current?.focus(), []);
  useEffect(() => {
    void loadGenres().then(setAllGenres).catch(() => setAllGenres([]));
  }, []);
  const pickLiterary = (l: Literary | null) => {
    setLiterary(l);
    setNeedLiterary(false);
    // Genres that don't belong to the new literary genre come off (strict; Kristi, Oct 28).
    setGenres((gs) => gs.filter((g) => genreFits(allGenres.find((x) => x.name === g)?.literary ?? null, l)));
  };

  const runSearch = async (q: string) => {
    const t = q.trim();
    if (t.length < 2 || t === searched.current) return;
    searched.current = t;
    setSearching(true);
    setSearchError(null);
    try {
      const a = await searchFor(t);
      if (searched.current === t) setAnswer(a);
    } catch (e) {
      if (searched.current === t) setSearchError(e instanceof Error ? e.message : String(e));
    } finally {
      if (searched.current === t) setSearching(false);
    }
  };

  // Search Open Library and Google Books once typing pauses.
  useEffect(() => {
    if (picked || manual) return;
    const t = setTimeout(() => void runSearch(text), PAUSE_MS);
    return () => clearTimeout(t);
  }, [text, picked, manual]);

  // Where the book would land on the shelf.
  useEffect(() => {
    if (!ordered(shelf)) {
      setEdge(null);
      return;
    }
    let live = true;
    const k = /^\d+$/.test(place) ? Number(place) : undefined;
    const t = setTimeout(() => {
      shelfEdge(shelf, k)
        .then((e) => live && setEdge(e))
        .catch(() => live && setEdge(null));
    }, k ? 300 : 0);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [shelf, place]);

  const pick = (f: Found) => {
    setPicked(f);
    setManual(null);
    setFailed(null);
    setCopy(defaultCopy(f.kind ?? answer?.read.kind ?? null));
    pickLiterary(defaultLiterary(f));
    if (f.openLibraryId && !f.summary) {
      void detailsFor(f.openLibraryId).then((d) => {
        if (!d.summary && !d.subtitle) return;
        setPicked((p) => (p && p.key === f.key ? { ...p, summary: p.summary ?? d.summary, summarySource: p.summarySource ?? d.summarySource, subtitle: p.subtitle ?? d.subtitle } : p));
      });
    }
  };
  const byHand = () => {
    setPicked(null);
    setFailed(null);
    // What you typed, unless it was a link (then the words the link held).
    const typed = text.trim();
    const words = /^https?:|^(www\.)?(amazon|audible|goodreads)\./i.test(typed) ? (answer?.read.words ?? '') : typed;
    setManual({ title: words, authors: '' });
    setCopy(defaultCopy(answer?.read.kind ?? null));
  };
  const back = () => {
    setPicked(null);
    setManual(null);
    setFailed(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const chosen = Boolean(picked || manual);
  const manualOk = manual ? manual.title.trim().length > 0 && splitAuthors(manual.authors).length > 0 : true;
  const title = picked?.title ?? manual?.title.trim() ?? '';

  const openBook = (label: number) => {
    const from = route.path.startsWith('/book/') ? route.state.from : route.path;
    navigate(`/book/${bookLabel(label)}`, { state: { from, full: route.state.full }, keepScroll: true });
  };

  const add = async () => {
    if (!chosen || !manualOk || busy) return;
    if (!literary) {
      setNeedLiterary(true);
      document.getElementById('add-literary')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    setBusy(true);
    setFailed(null);
    const read = answer?.read;
    const sent = addDetails(picked, manual ? { title: manual.title, authors: splitAuthors(manual.authors) } : null, {
      literaryGenre: literary,
      finished: { ended, rating, readAgain },
      notForMe: { chips: nfmChips, reason: nfmReason },
      audience,
      genres,
      copy,
      copyDetails: details,
      shelf,
      place: { place },
      interest,
      why,
      recommendedBy: rec,
      goodreadsId: read?.goodreadsId || undefined,
      asin: read?.asin || undefined,
      audibleId: read?.audibleId || undefined,
    });
    try {
      const added = await addBook(sent, fromList?.entry);
      const label = Number(added.label.replace(/^B0*/, ''));
      const from = route.path.startsWith('/book/') ? route.state.from : route.path;
      onClose();
      // From a list you stay on the list (its row now says the shelf); otherwise the book opens.
      if (!fromList) openBook(label);
      notice({
        message: `${title} added to ${shelfName(shelf)}`,
        undo: () =>
          void undoAdd(added.actions)
            .then(() => {
              if (!fromList) navigate(from ?? '/shelves', { replace: true });
              notice({ message: `Undone: ${title} isn’t on your list` });
            })
            .catch((e) => notice({ message: `Couldn’t undo: ${e instanceof Error ? e.message : String(e)}`, done: false })),
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setFailed({ message, label: alreadyLabel(message) });
      setBusy(false);
    }
  };

  const results = answer?.results ?? [];
  const sameAsTyped = answer && searched.current === text.trim();
  const libraryListen = details.libraryListen;

  return (
    <>
      <button type="button" class="sheet-scrim" aria-label="Cancel" tabIndex={-1} onClick={onClose} />
      <div class="add-panel" role="dialog" aria-modal="true" aria-label="Add a book" onKeyDown={(e) => e.key === 'Escape' && onClose()}>
        <div class="add-head">
          <h2 class="add-title">Add a book</h2>
          <button type="button" class="round-btn add-close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div class="add-body">
          {/* 1. Find it */}
          {!chosen ? (
            <section class="add-find" aria-label="Find the book">
              <form
                class="add-search"
                role="search"
                onSubmit={(e) => {
                  e.preventDefault();
                  searched.current = '';
                  void runSearch(text);
                }}
              >
                <label for="add-q" class="add-label">
                  Title, author, ISBN, or an Amazon, Audible or Goodreads link
                </label>
                <div class="add-search-row">
                  <input
                    id="add-q"
                    ref={inputRef}
                    type="search"
                    enterKeyHint="search"
                    autoComplete="off"
                    value={text}
                    onInput={(e) => setText((e.target as HTMLInputElement).value)}
                  />
                  <button type="submit" class="btn-main add-search-btn">
                    Search
                  </button>
                </div>
              </form>

              {onList && onList.length > 0 ? (
                <div class="add-onlist" role="note">
                  <p class="add-quiet">Already on your list:</p>
                  <ul>
                    {onList.slice(0, 3).map((b) => (
                      <li key={b.label}>
                        <span class="add-onlist-title">{b.title}</span> · {bookLabel(b.label)} · {statusName(b.status)}{' '}
                        <button
                          type="button"
                          class="btn-link"
                          onClick={() => {
                            onClose();
                            openBook(b.label);
                          }}
                        >
                          Open it ›
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {searching ? <p class="add-quiet">Searching Open Library and Google Books…</p> : null}
              {searchError ? <p class="add-error">{searchError}</p> : null}
              {!searching && sameAsTyped && results.length ? (
                <>
                  <p class="add-quiet">Matches from Open Library and Google Books</p>
                  <ul class="add-results">
                    {results.map((f) => (
                      <li key={f.key}>
                        <button type="button" class="add-result" onClick={() => pick(f)}>
                          <Cover title={f.title} url={f.coverUrl} size="sm" />
                          <span class="add-result-text">
                            <span class="add-result-title">{f.title}</span>
                            {f.subtitle ? <span class="add-result-sub">{f.subtitle}</span> : null}
                            <span class="add-result-line">{foundLine(f)}</span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {!searching && sameAsTyped && !results.length && answer?.message ? <p class="add-quiet">{answer.message}</p> : null}
              {text.trim().length >= 2 || answer ? (
                <button type="button" class="btn-link add-byhand" onClick={byHand}>
                  Not here? Add it by hand
                </button>
              ) : null}
            </section>
          ) : (
            <section class="add-chosen" aria-label="The book">
              {picked ? (
                <div class="add-picked">
                  <Cover title={picked.title} url={picked.coverUrl} size="sm" />
                  <div class="add-result-text">
                    <span class="add-result-title">{picked.title}</span>
                    {picked.subtitle ? <span class="add-result-sub">{picked.subtitle}</span> : null}
                    <span class="add-result-line">{foundLine(picked)}</span>
                    <span class="add-quiet">From {picked.source}</span>
                  </div>
                </div>
              ) : manual ? (
                <div class="add-manual">
                  <label class="add-field">
                    <span class="add-label">Title</span>
                    <input type="text" maxLength={1000} value={manual.title} onInput={(e) => setManual({ ...manual, title: (e.target as HTMLInputElement).value })} />
                  </label>
                  <label class="add-field">
                    <span class="add-label">Author (several: separate with commas)</span>
                    <input type="text" maxLength={1000} value={manual.authors} onInput={(e) => setManual({ ...manual, authors: (e.target as HTMLInputElement).value })} />
                  </label>
                </div>
              ) : null}
              <button type="button" class="btn-link" onClick={back}>
                ← Search again
              </button>
            </section>
          )}

          {chosen ? (
            <>
              {/* The book: literary genre (required), audience, your genres (optional) */}
              <section class="add-sec" aria-label="About the book" id="add-literary">
                <p class="add-label">Literary genre {needLiterary ? <span class="add-error">· Pick a literary genre first</span> : null}</p>
                <ChoiceChips label="Literary genre" choices={LITERARY_CHOICES.map((l) => [l.key, l.name] as const)} value={literary} onPick={pickLiterary} clearable />
                <p class="add-label add-label-sub">Audience</p>
                <ChoiceChips label="Audience" choices={AUDIENCE_CHOICES.map((a) => [a.key, a.name] as const)} value={audience} onPick={setAudience} clearable />
                <p class="add-label add-label-sub">
                  Your genres <span class="add-quiet">· optional{literary ? '' : '; pick the literary genre to narrow them'}</span>
                </p>
                <ToggleChips label="Your genres" choices={genreChoices(allGenres, literary, genres)} on={genres} onChange={setGenres} />
              </section>

              {/* 2. Your copy */}
              <section class="add-sec" aria-label="Your copy">
                <p class="add-label">Your copy</p>
                <ChoiceChips label="Your copy" choices={COPY_CHOICES.map((c) => [c.key, c.label] as const)} value={copy} onPick={(v) => v && setCopy(v)} />
                {copy !== 'none' ? (
                  <>
                    <button type="button" class="add-fold" aria-expanded={showDetails} onClick={() => setShowDetails(!showDetails)}>
                      {showDetails ? '▾' : '▸'} {copy === 'library' ? 'Borrowed or on hold, Libby audio' : 'How you got it, bought from, price, found through'}
                    </button>
                    {showDetails ? (
                      copy === 'library' ? (
                        <div class="add-details">
                          <ChoiceChips
                            label="Library step"
                            choices={[
                              ['on_hold', 'Placed a hold'],
                              ['borrowed', 'Borrowed now'],
                            ] as const}
                            value={details.loanStep}
                            onPick={(k) => k && setDetails({ ...details, loanStep: k })}
                          />
                          <ChoiceChips
                            label="Library format"
                            choices={[
                              [false, 'Kindle (Libby)'],
                              [true, 'Libby audio'],
                            ] as const}
                            value={libraryListen}
                            onPick={(k) => k !== null && setDetails({ ...details, libraryListen: k })}
                          />
                          {details.loanStep === 'borrowed' ? (
                            <ChoiceChips label="Loan length" choices={[7, 14, 21].map((d) => [d, `${d} days`] as const)} value={details.loanDays} onPick={(d) => d && setDetails({ ...details, loanDays: d })} />
                          ) : null}
                        </div>
                      ) : (
                        <div class="add-details">
                          <ChoiceChips
                            label="How"
                            choices={[
                              ['bought', 'I bought it'],
                              ['gift', 'A gift'],
                              ['shared', 'Shared with me'],
                              ['free', 'Free'],
                            ] as const}
                            value={details.how}
                            onPick={(k) => k && setDetails({ ...details, how: k, from: '' })}
                          />
                          {details.how === 'bought' ? (
                            <label class="add-field">
                              <span class="add-label">Bought from</span>
                              <input type="text" maxLength={100} value={details.store || storeFor(copy)} onInput={(e) => setDetails({ ...details, store: (e.target as HTMLInputElement).value })} />
                            </label>
                          ) : null}
                          {hasFrom(details.how) ? <FromField how={details.how} value={details.from ?? ''} onChange={(v) => setDetails({ ...details, from: v })} cls="add-field" labelCls="add-label" /> : null}
                          <label class="add-field">
                            <span class="add-label">Price paid</span>
                            <input type="text" inputMode="decimal" maxLength={12} value={details.price} onInput={(e) => setDetails({ ...details, price: (e.target as HTMLInputElement).value })} />
                          </label>
                          <label class="add-field">
                            <span class="add-label">Found through (only you see this)</span>
                            <input type="text" maxLength={100} placeholder="BookBub, Kindle Daily Deal…" value={details.foundThrough} onInput={(e) => setDetails({ ...details, foundThrough: (e.target as HTMLInputElement).value })} />
                          </label>
                        </div>
                      )
                    ) : null}
                  </>
                ) : null}
              </section>

              {/* 3. Where it goes */}
              <section class="add-sec" aria-label="Where it goes">
                <p class="add-label">Where it goes</p>
                <ChoiceChips label="Where it goes" choices={SHELF_CHOICES.map((s) => [s.key, s.label] as const)} value={shelf} onPick={(k) => k && setShelf(k)} />
                {ordered(shelf) ? (
                  <>
                    <p class="add-label add-label-sub">Where on {shelfName(shelf)}</p>
                    <ChoiceChips
                      label={`Where on ${shelfName(shelf)}`}
                      choices={[
                        ['top', 'Top'],
                        ['bottom', 'Bottom'],
                      ] as const}
                      value={place === 'top' || place === 'bottom' ? place : null}
                      onPick={(k) => k && setPlace(k)}
                    >
                      <label class={`chip bk-place-n${/^\d+$/.test(place) ? ' on' : ''}`}>
                        #
                        <input
                          type="number"
                          min={1}
                          inputMode="numeric"
                          aria-label="Place number"
                          value={n}
                          onInput={(e) => {
                            const v = (e.target as HTMLInputElement).value;
                            setN(v);
                            if (/^\d+$/.test(v) && Number(v) > 0) setPlace(v);
                          }}
                        />
                      </label>
                    </ChoiceChips>
                    <p class="add-quiet add-where">{placeLine(place, shelf, edge)}</p>
                  </>
                ) : shelf === 'currently_reading' ? (
                  <p class="add-quiet">It goes to the top of Currently Reading{copy !== 'none' ? ', reading your new copy' : ''}.</p>
                ) : null}
                {/* More choices (Kristi, Oct 28): you've read it, or decided not to. */}
                <button type="button" class="add-fold" aria-expanded={showMore || shelf === 'finished' || shelf === 'not_for_me'} onClick={() => setShowMore(!showMore)}>
                  {showMore || shelf === 'finished' || shelf === 'not_for_me' ? '▾' : '▸'} More choices: finished, or not for me
                </button>
                {showMore || shelf === 'finished' || shelf === 'not_for_me' ? (
                  <ChoiceChips label="More choices" choices={MORE_CHOICES.map((s) => [s.key, s.label] as const)} value={shelf} onPick={(k) => setShelf(k ?? 'future_reads')} clearable />
                ) : null}
                {shelf === 'finished' ? (
                  <div class="add-details" aria-label="Finished">
                    <p class="add-label add-label-sub">When you finished it (optional)</p>
                    <FuzzyEditor label="Finished" value={ended} onChange={setEnded} />
                    <p class="add-label add-label-sub">Your rating (optional)</p>
                    <Stars value={rating} onChange={setRating} hint />
                    <label class="add-check">
                      <input type="checkbox" checked={readAgain} onChange={(e) => setReadAgain((e.target as HTMLInputElement).checked)} /> ↻ Read again in future
                    </label>
                  </div>
                ) : shelf === 'not_for_me' ? (
                  <div class="add-details" aria-label="Not for me">
                    <p class="add-quiet">Counts as Passed on: you decided not to read it. The book page and History keep why.</p>
                    <ToggleChips label="Reasons" choices={NOT_FOR_ME_CHIPS} on={nfmChips} onChange={setNfmChips} />
                    <label class="add-field">
                      <span class="add-label">Why, in your words (optional; only you see this)</span>
                      <input type="text" maxLength={2000} value={nfmReason} onInput={(e) => setNfmReason((e.target as HTMLInputElement).value)} />
                    </label>
                  </div>
                ) : null}
              </section>

              <section class="add-sec" aria-label="Your notes">
                <p class="add-label">
                  Interest <span class="add-quiet">· {interestHint(interest)}</span>
                </p>
                <InterestDots value={interest} hints={INTEREST_HINTS} onChange={setInterest} />
                <label class="add-field">
                  <span class="add-label">Why I want to read it</span>
                  <input type="text" maxLength={5000} value={why} onInput={(e) => setWhy((e.target as HTMLInputElement).value)} />
                </label>
                <label class="add-field">
                  <span class="add-label">Recommended by</span>
                  <input type="text" maxLength={1000} placeholder="A friend, a podcast, a list" value={rec} onInput={(e) => setRec((e.target as HTMLInputElement).value)} />
                </label>
              </section>
            </>
          ) : null}
        </div>

        <div class="add-foot">
          {failed ? (
            <p class="add-error" role="alert">
              {failed.label !== null ? (
                <>
                  Already on your list as {bookLabel(failed.label)}.{' '}
                  <button
                    type="button"
                    class="btn-link"
                    onClick={() => {
                      onClose();
                      openBook(failed.label!);
                    }}
                  >
                    Open it ›
                  </button>
                </>
              ) : (
                `Not added: ${failed.message}`
              )}
            </p>
          ) : null}
          {chosen && !manualOk ? <p class="add-quiet">A title and an author, then Add.</p> : null}
          <div class="add-buttons">
            <button type="button" class="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            {chosen ? (
              <button type="button" class="btn-main" disabled={busy || !manualOk} onClick={() => void add()}>
                {busy ? 'Adding…' : addButton(shelf)}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
